import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gameService } from '../services/gameService.ts';
import {
  FIRESWEEPER_CONTAINER,
  FIRESWEEPER_SURVIVOR,
  SHARED_FIRE_MECHANICS,
} from '../constants/gameModes.ts';
import { FirefrontLogo } from '../components/FirefrontLogo.tsx';
import {
  ModeDemoLoop,
  CONTAINER_DEMO_FRAMES,
  CONTAINER_DEMO_STEPS,
  SURVIVOR_DEMO_FRAMES,
  SURVIVOR_DEMO_STEPS,
} from '../components/HowToPlayDemo.tsx';
import { useUtcCountdown } from '../hooks/useUtcCountdown.ts';

export default function Home() {
  const navigate = useNavigate();
  const { dayKey } = useUtcCountdown();
  const [containerGamesPlayed, setContainerGamesPlayed] = useState<number | null>(null);
  const [survivorGamesPlayed, setSurvivorGamesPlayed] = useState<number | null>(null);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [containerCount, survivorCount] = await Promise.all([
          gameService.getSurvivalGamesCount(dayKey),
          gameService.getPortfolioGamesCount(dayKey),
        ]);
        setContainerGamesPlayed(containerCount);
        setSurvivorGamesPlayed(survivorCount);
      } catch (error) {
        console.error('Error fetching games played:', error);
        setContainerGamesPlayed(0);
        setSurvivorGamesPlayed(0);
      }
    };
    fetchCounts();
  }, [dayKey]);

  return (
    <div className="home-page">
      <section className="home-hero home-hero--compact">
        <FirefrontLogo className="home-hero-logo" size={72} />
        <h1 className="home-title">Firesweeper</h1>
      </section>

      <div className="home-modes">
        <section className="home-mechanics" aria-labelledby="home-mechanics-heading">
          <h2 id="home-mechanics-heading" className="home-mechanics-heading">
            Game Mechanics
          </h2>
          <ul className="home-mechanics-list">
            {SHARED_FIRE_MECHANICS.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </section>

        <section className="game-type-grid">
        <article className="game-type-card game-type-card-survival">
          <header className="game-type-header">
            <h2 className="game-type-title">{FIRESWEEPER_CONTAINER.subtitle}</h2>
            <p className="game-type-tagline">{FIRESWEEPER_CONTAINER.tagline}</p>
          </header>

          <button
            type="button"
            className="btn btn-primary btn-play-cta"
            onClick={() => navigate(FIRESWEEPER_CONTAINER.path)}
          >
            Play Container
          </button>

          <p className="game-type-description">{FIRESWEEPER_CONTAINER.description}</p>

          <ModeDemoLoop
            frames={CONTAINER_DEMO_FRAMES}
            steps={CONTAINER_DEMO_STEPS}
          />

          <footer className="game-type-footer">
            <p className="game-type-stat">
              {containerGamesPlayed !== null ? (
                `${containerGamesPlayed.toLocaleString()} plays today`
              ) : (
                <span className="stat-shimmer" aria-label="Loading plays today" />
              )}
            </p>
            <button
              type="button"
              className="btn btn-secondary btn-compact"
              onClick={() => navigate(FIRESWEEPER_CONTAINER.leaderboardPath)}
            >
              Leaderboard
            </button>
          </footer>
        </article>

        <article className="game-type-card game-type-card-portfolio">
          <header className="game-type-header">
            <h2 className="game-type-title">{FIRESWEEPER_SURVIVOR.subtitle}</h2>
            <p className="game-type-tagline">{FIRESWEEPER_SURVIVOR.tagline}</p>
          </header>

          <button
            type="button"
            className="btn btn-primary btn-play-cta"
            onClick={() => navigate(FIRESWEEPER_SURVIVOR.path)}
          >
            Play Survivor
          </button>

          <p className="game-type-description">{FIRESWEEPER_SURVIVOR.description}</p>

          <ModeDemoLoop
            frames={SURVIVOR_DEMO_FRAMES}
            steps={SURVIVOR_DEMO_STEPS}
          />

          <footer className="game-type-footer">
            <p className="game-type-stat">
              {survivorGamesPlayed !== null ? (
                `${survivorGamesPlayed.toLocaleString()} plays today`
              ) : (
                <span className="stat-shimmer" aria-label="Loading plays today" />
              )}
            </p>
            <button
              type="button"
              className="btn btn-secondary btn-compact"
              onClick={() => navigate(FIRESWEEPER_SURVIVOR.leaderboardPath)}
            >
              Leaderboard
            </button>
          </footer>
        </article>
        </section>
      </div>
    </div>
  );
}
